import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Edit, Save, X, Upload, Calendar, FileText, Image as ImageIcon, Sparkles, CheckCircle, AlertCircle, Package, Briefcase, LogOut, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SimpleAdmin = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('works');
  
  // Works state
  const [works, setWorks] = useState([]);
  const [editingWorkId, setEditingWorkId] = useState(null);
  const [isAddingWork, setIsAddingWork] = useState(false);
  const [workFormData, setWorkFormData] = useState({
    title: '',
    date: '',
    shortDesc: '',
    mainImage: '',
    detailImages: [],
    fullDesc: ''
  });
  
  // Services state
  const [services, setServices] = useState([]);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [isAddingService, setIsAddingService] = useState(false);
  const [serviceFormData, setServiceFormData] = useState({
    name: '',
    description: [],
    price: '',
    icon: 'sparkles'
  });
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchWorks();
    fetchServices();
  }, []);

  const fetchWorks = async () => {
    try {
      const res = await fetch(`${API}/admin/current-works`);
      if (!res.ok) throw new Error('Failed to fetch works');
      const data = await res.json();
      console.log('Loaded works from API:', data?.length || 0);
      setWorks(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Fetch works error:', err);
      setWorks([]);
      toast.error('Chyba při načítání prací', { icon: '⚠️' });
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const res = await fetch(`${API}/admin/services`);
      if (!res.ok) throw new Error('Failed to fetch services');
      const data = await res.json();
      console.log('Loaded services from API:', data?.length || 0);
      const sorted = Array.isArray(data) ? data.sort((a, b) => (a.order || 0) - (b.order || 0)) : [];
      setServices(sorted);
    } catch (err) {
      console.error('Fetch services error:', err);
      setServices([]);
      toast.error('Chyba při načítání služeb', { icon: '⚠️' });
    }
  };

  // Works handlers
  const handleAddWork = () => {
    setIsAddingWork(true);
    setEditingWorkId(null);
    setWorkFormData({ title: '', date: '', shortDesc: '', mainImage: '', detailImages: [], fullDesc: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditWork = (work) => {
    setEditingWorkId(work.id);
    setIsAddingWork(false);
    setWorkFormData({
      title: work.title,
      date: work.date,
      shortDesc: work.shortDesc,
      mainImage: work.mainImage,
      detailImages: work.detailImages || [],
      fullDesc: work.fullDesc
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveWork = async () => {
    try {
      console.log('Saving work...', workFormData);
      
      if (isAddingWork) {
        const response = await fetch(`${API}/admin/current-works`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...workFormData, id: `work-${Date.now()}` })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Chyba při ukládání');
        }
        
        toast.success('Nová práce přidána!', { icon: '✅' });
      } else {
        const response = await fetch(`${API}/admin/current-works/${editingWorkId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(workFormData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Chyba při ukládání');
        }
        
        toast.success('Práce aktualizována!', { icon: '✅' });
      }
      
      await fetchWorks();
      setEditingWorkId(null);
      setIsAddingWork(false);
    } catch (err) {
      console.error('Save work error:', err);
      toast.error(`Chyba: ${err.message}`, { icon: '❌', duration: 5000 });
    }
  };

  const handleCancelWork = () => {
    setEditingWorkId(null);
    setIsAddingWork(false);
    setWorkFormData({ title: '', date: '', shortDesc: '', mainImage: '', detailImages: [], fullDesc: '' });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!res.ok) {
        throw new Error(`Upload failed: ${res.status}`);
      }
      
      const data = await res.json();
      setWorkFormData(prev => ({ ...prev, mainImage: data.url }));
      toast.success('Obrázek nahrán!', { icon: '✨' });
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Chyba při nahrávání: ' + err.message, { icon: '❌' });
    } finally {
      setUploading(false);
    }
  };

  const addDetailImage = () => {
    setWorkFormData(prev => ({
      ...prev,
      detailImages: [...(prev.detailImages || []), '']
    }));
  };

  const updateDetailImage = (idx, value) => {
    const newImages = [...(workFormData.detailImages || [])];
    newImages[idx] = value;
    setWorkFormData({ ...workFormData, detailImages: newImages });
  };

  const removeDetailImage = (idx) => {
    const newImages = (workFormData.detailImages || []).filter((_, i) => i !== idx);
    setWorkFormData({ ...workFormData, detailImages: newImages });
  };

  // Services handlers
  const handleAddService = () => {
    setIsAddingService(true);
    setEditingServiceId(null);
    setServiceFormData({ name: '', description: [], price: '', icon: 'sparkles' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditService = (service) => {
    setEditingServiceId(service.id);
    setIsAddingService(false);
    setServiceFormData({
      name: service.name,
      description: service.description || [],
      price: service.price,
      icon: service.icon || 'sparkles'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSaveService = async () => {
    try {
      console.log('Saving service...', serviceFormData);
      
      if (isAddingService) {
        const maxOrder = Math.max(...services.map(s => s.order || 0), 0);
        const response = await fetch(`${API}/admin/services`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            ...serviceFormData, 
            id: `service-${Date.now()}`,
            order: maxOrder + 1
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Chyba při ukládání');
        }
        
        toast.success('Nová služba přidána!', { icon: '✅' });
      } else {
        const response = await fetch(`${API}/admin/services/${editingServiceId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(serviceFormData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Chyba při ukládání');
        }
        
        toast.success('Služba aktualizována!', { icon: '✅' });
      }
      
      await fetchServices();
      setEditingServiceId(null);
      setIsAddingService(false);
    } catch (err) {
      console.error('Save service error:', err);
      toast.error(`Chyba: ${err.message}`, { icon: '❌', duration: 5000 });
    }
  };

  const handleCancelService = () => {
    setEditingServiceId(null);
    setIsAddingService(false);
    setServiceFormData({ name: '', description: [], price: '', icon: 'sparkles' });
  };

  const handleDescriptionChange = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    setServiceFormData({ ...serviceFormData, description: lines });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-b-4 border-cyan-400 mx-auto"></div>
          <p className="mt-6 text-white font-bold text-xl">Načítání...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8 sm:py-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-xl border border-cyan-400/30">
            <Sparkles className="text-cyan-400 animate-pulse" size={24} />
            <span className="text-cyan-400 font-bold text-sm sm:text-base tracking-wider uppercase">
              Premium Admin Panel
            </span>
            <Sparkles className="text-blue-400 animate-pulse" size={24} />
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
            Správa Obsahu
          </h1>

          {/* Logout Button */}
          <button
            onClick={async () => {
              await logout();
              navigate('/login');
            }}
            className="mt-4 px-6 py-2 rounded-xl bg-slate-700/50 hover:bg-slate-600/50 text-white font-bold transition-all flex items-center gap-2 mx-auto"
          >
            <LogOut size={18} />
            Odhlásit se
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 max-w-md mx-auto">
          <button
            onClick={() => {
              setActiveTab('works');
              setEditingWorkId(null);
              setEditingServiceId(null);
            }}
            className={`flex-1 py-3 px-6 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'works'
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50'
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800'
            }`}
          >
            <Briefcase size={20} />
            Práce
          </button>
          <button
            onClick={() => {
              setActiveTab('services');
              setEditingWorkId(null);
              setEditingServiceId(null);
            }}
            className={`flex-1 py-3 px-6 rounded-xl font-bold transition-all duration-300 flex items-center justify-center gap-2 ${
              activeTab === 'services'
                ? 'bg-gradient-to-r from-purple-500 to-pink-600 text-white shadow-lg shadow-purple-500/50'
                : 'bg-slate-800/50 text-slate-400 hover:bg-slate-800'
            }`}
          >
            <Package size={20} />
            Služby
          </button>
        </div>

        {/* WORKS TAB */}
        {activeTab === 'works' && (
          <>
            {(editingWorkId || isAddingWork) ? (
              <div className="max-w-4xl mx-auto mb-12">
                <Card className="bg-slate-800/90 backdrop-blur-2xl border-2 border-cyan-500/30 shadow-2xl">
                  <CardHeader className="bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-purple-500/20 border-b-2 border-cyan-500/30">
                    <CardTitle className="text-2xl font-black text-cyan-400 flex items-center gap-3">
                      <Edit size={24} />
                      {isAddingWork ? 'Přidat novou práci' : 'Editace práce'}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="p-6 space-y-6">
                    <div className="space-y-2">
                      <Label className="text-cyan-400 font-bold">Název práce</Label>
                      <Input
                        value={workFormData.title}
                        onChange={(e) => setWorkFormData({ ...workFormData, title: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-cyan-400 font-bold">Datum</Label>
                      <Input
                        value={workFormData.date}
                        onChange={(e) => setWorkFormData({ ...workFormData, date: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-cyan-400 font-bold">Krátký popis</Label>
                      <Textarea
                        value={workFormData.shortDesc}
                        onChange={(e) => setWorkFormData({ ...workFormData, shortDesc: e.target.value })}
                        rows={3}
                        className="bg-slate-900/50 border-slate-700 text-white"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-cyan-400 font-bold">Hlavní fotka (URL)</Label>
                      <Input
                        value={workFormData.mainImage}
                        onChange={(e) => setWorkFormData({ ...workFormData, mainImage: e.target.value })}
                        placeholder="Vložte URL obrázku (např. z imgbb.com)"
                        className="bg-slate-900/50 border-slate-700 text-white"
                      />
                      {workFormData.mainImage && (
                        <img src={workFormData.mainImage} alt="Preview" className="mt-2 h-32 object-cover rounded-lg" />
                      )}
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-cyan-400 font-bold">Další fotky (galerie)</Label>
                        <button
                          type="button"
                          onClick={addDetailImage}
                          className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 text-white text-sm rounded-lg flex items-center gap-1"
                        >
                          <Plus size={16} />
                          Přidat fotku
                        </button>
                      </div>
                      
                      {(workFormData.detailImages || []).map((img, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={img}
                            onChange={(e) => updateDetailImage(idx, e.target.value)}
                            placeholder={`URL fotky ${idx + 1}`}
                            className="bg-slate-900/50 border-slate-700 text-white flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => removeDetailImage(idx)}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                      {(workFormData.detailImages || []).length > 0 && (
                        <div className="grid grid-cols-4 gap-2 mt-2">
                          {workFormData.detailImages.filter(img => img).map((img, idx) => (
                            <img key={idx} src={img} alt={`Detail ${idx + 1}`} className="h-20 w-full object-cover rounded-lg" />
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-cyan-400 font-bold">Detailní popis</Label>
                      <Textarea
                        value={workFormData.fullDesc}
                        onChange={(e) => setWorkFormData({ ...workFormData, fullDesc: e.target.value })}
                        rows={8}
                        className="bg-slate-900/50 border-slate-700 text-white font-mono"
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleSaveWork}
                        className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold rounded-xl flex items-center justify-center gap-2"
                      >
                        <Save size={20} />
                        Uložit
                      </button>
                      <button
                        onClick={handleCancelWork}
                        className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl flex items-center gap-2"
                      >
                        <X size={20} />
                        Zrušit
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                <div className="mb-6 flex justify-center">
                  <button
                    onClick={handleAddWork}
                    className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg"
                  >
                    <Plus size={20} />
                    Přidat novou práci
                  </button>
                </div>
                <div className="grid gap-6">
                  {works && works.length > 0 ? (
                    works.map((work) => (
                      <Card
                        key={work.id}
                        className="bg-slate-800/60 border-slate-700 hover:border-cyan-500/50 transition-all"
                      >
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-64 h-48 overflow-hidden bg-slate-900">
                            <img
                              src={work.mainImage}
                              alt={work.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 p-6">
                            <h3 className="text-2xl font-bold text-cyan-400 mb-2">{work.title}</h3>
                            <p className="text-slate-400 text-sm mb-3">{work.date}</p>
                            <p className="text-slate-300 mb-4">{work.shortDesc}</p>
                            <button
                              onClick={() => handleEditWork(work)}
                              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-bold rounded-lg flex items-center gap-2"
                            >
                              <Edit size={18} />
                              Upravit
                            </button>
                          </div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="text-center py-12 text-slate-400">
                      <p>Žádné práce. Klikněte "Přidat novou práci".</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* SERVICES TAB */}
        {activeTab === 'services' && (
          <>
            {(editingServiceId || isAddingService) ? (
              <div className="max-w-4xl mx-auto mb-12">
                <Card className="bg-slate-800/90 backdrop-blur-2xl border-2 border-purple-500/30 shadow-2xl">
                  <CardHeader className="bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 border-b-2 border-purple-500/30">
                    <CardTitle className="text-2xl font-black text-purple-400 flex items-center gap-3">
                      <Package size={24} />
                      {isAddingService ? 'Přidat novou službu' : 'Editace služby'}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="p-6 space-y-6">
                    <div className="space-y-2">
                      <Label className="text-purple-400 font-bold">Název služby</Label>
                      <Input
                        value={serviceFormData.name}
                        onChange={(e) => setServiceFormData({ ...serviceFormData, name: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                        placeholder="Kompletní čištění interiéru"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-purple-400 font-bold">Ikona/Obrázek služby (URL)</Label>
                      <Input
                        value={serviceFormData.icon || ''}
                        onChange={(e) => setServiceFormData({ ...serviceFormData, icon: e.target.value })}
                        placeholder="Vložte URL obrázku"
                        className="bg-slate-900/50 border-slate-700 text-white"
                      />
                      {serviceFormData.icon && serviceFormData.icon.startsWith('http') && (
                        <img src={serviceFormData.icon} alt="Preview" className="mt-2 h-24 object-cover rounded-lg" />
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label className="text-purple-400 font-bold">Cena</Label>
                      <Input
                        value={serviceFormData.price}
                        onChange={(e) => setServiceFormData({ ...serviceFormData, price: e.target.value })}
                        className="bg-slate-900/50 border-slate-700 text-white"
                        placeholder="od 2500,-"
                      />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Label className="text-purple-400 font-bold">Popis služby</Label>
                        <button
                          type="button"
                          onClick={() => {
                            const newDesc = [...(serviceFormData.description || []), ''];
                            setServiceFormData({ ...serviceFormData, description: newDesc });
                          }}
                          className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg"
                        >
                          + Přidat řádek
                        </button>
                      </div>
                      
                      {(serviceFormData.description || ['']).map((item, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={item}
                            onChange={(e) => {
                              const newDesc = [...(serviceFormData.description || [])];
                              newDesc[idx] = e.target.value;
                              setServiceFormData({ ...serviceFormData, description: newDesc });
                            }}
                            placeholder={`Položka ${idx + 1}`}
                            className="bg-slate-900/50 border-slate-700 text-white flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newDesc = (serviceFormData.description || []).filter((_, i) => i !== idx);
                              setServiceFormData({ ...serviceFormData, description: newDesc });
                            }}
                            className="px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <button
                        onClick={handleSaveService}
                        className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white font-bold rounded-xl flex items-center justify-center gap-2"
                      >
                        <Save size={20} />
                        Uložit
                      </button>
                      <button
                        onClick={handleCancelService}
                        className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-xl flex items-center gap-2"
                      >
                        <X size={20} />
                        Zrušit
                      </button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <>
                <div className="mb-6 flex justify-center">
                  <button
                    onClick={handleAddService}
                    className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg"
                  >
                    <Plus size={20} />
                    Přidat novou službu
                  </button>
                </div>
                <div className="grid gap-6 max-w-4xl mx-auto">
                  {services && services.length > 0 ? (
                    services.map((service) => (
                      <Card
                      key={service.id}
                    className="bg-slate-800/60 border-slate-700 hover:border-purple-500/50 transition-all"
                  >
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-purple-400 mb-2">{service.name}</h3>
                          <p className="text-2xl font-black text-cyan-400 mb-3">{service.price}</p>
                        </div>
                        <button
                          onClick={() => handleEditService(service)}
                          className="px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white font-bold rounded-lg flex items-center gap-2"
                        >
                          <Edit size={18} />
                          Upravit
                        </button>
                      </div>
                      <ul className="space-y-2">
                        {service.description && Array.isArray(service.description) && service.description.map((item, idx) => (
                          <li key={idx} className="text-slate-300 flex items-start gap-2">
                            <CheckCircle size={16} className="text-emerald-400 mt-1 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <p>Žádné služby. Klikněte &quot;Přidat novou službu&quot;.</p>
                  </div>
                )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(50px, 50px) scale(1.05); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
};
